/**
 * Tempo 协议链接组件
 * 根据实体类型显示不同图标：项目显示文件夹图标，任务显示可操作的 checkbox
 */

import React, { ReactNode, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { globalTaskEditAtom } from '../../atoms/v3/taskAtoms';
import { tasksAtom, updateTaskAtom } from '../../atoms/v0/taskAtoms';
import { createTempoProtocolHandler } from '../../utils/tempoProtocolHandler';
import { isTempoUrl, parseTempoUrl } from '../../constants/tempoProtocol';
import { trackEvent } from '../../lib/analytics';

interface TempoProtocolLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
}

/**
 * 解析链接文本，支持别名格式：[显示文本|真实名称](tempo://...)
 */
const parseLinkText = (children: ReactNode): { displayText: string; alias?: string } => {
  if (typeof children === 'string') {
    // 检查是否包含别名格式
    const aliasMatch = children.match(/^(.+?)\|(.+)$/);
    if (aliasMatch) {
      return {
        displayText: aliasMatch[1].trim(),
        alias: aliasMatch[2].trim()
      };
    }
    return { displayText: children };
  }

  // 如果不是字符串，直接返回
  return { displayText: String(children) };
};



export const TempoProtocolLink: React.FC<TempoProtocolLinkProps> = ({
  href,
  children,
  className = ''
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['v3/common']);
  const [, setGlobalTaskEdit] = useAtom(globalTaskEditAtom);
  const [tasks] = useAtom(tasksAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [isUpdating, setIsUpdating] = useState(false);

  // 创建协议处理器
  const protocolHandler = useMemo(
    () => createTempoProtocolHandler(navigate, setGlobalTaskEdit),
    [navigate, setGlobalTaskEdit]
  );

  // 解析链接文本和URL（使用 useMemo 缓存）
  const { displayText, alias } = useMemo(() => parseLinkText(children), [children]);
  const urlInfo = useMemo(() => parseTempoUrl(href), [href]);

  // 如果是任务，获取任务信息（使用 useMemo 缓存）
  const task = useMemo(() => {
    return urlInfo?.isValid && urlInfo.entityType === 'task'
      ? tasks.find(t => t.id === urlInfo.entityId)
      : null;
  }, [urlInfo, tasks]);

  // 处理任务 checkbox 点击
  const handleTaskToggle = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!task || isUpdating) return;

    setIsUpdating(true);
    const newDoneState = !task.done;

    // 记录任务状态切换事件
    trackEvent(newDoneState ? 'task completed' : 'task uncompleted', {
      task_id: task.id,
      task_title: task.title.substring(0, 50),
      from_tempo_link: true
    });

    try {
      const success = await updateTask({
        id: task.id,
        done: newDoneState,
      });

      if (!success) {
        console.error('Failed to update task:', task.id);
      }
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [task, isUpdating, updateTask, setIsUpdating]);

  // 处理点击事件（导航）
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!isTempoUrl(href)) {
      console.warn('Not a tempo:// URL:', href);
      return;
    }

    const success = protocolHandler.handleTempoNavigation(href);
    if (!success) {
      console.error('Failed to handle tempo navigation:', href);
    }
  };

  // 基础样式类
  const baseClasses = [
    'inline-flex',
    'items-center',
    'px-2',
    'py-1',
    'rounded-md',
    'text-sm', // 从 text-xs 改为 text-sm，和普通对话文本一样大
    'font-medium',
    'cursor-pointer',
    'transition-all',
    'duration-200',
    'no-underline',
    // 颜色样式
    'bg-ios-blue/10',
    'text-ios-blue',
    'dark:bg-ios-dark-blue/20',
    'dark:text-ios-dark-blue',
    // 悬停效果
    'hover:bg-ios-blue/20',
    'dark:hover:bg-ios-dark-blue/30',
    // 点击效果
    'active:scale-95'
  ].join(' ');

  // 渲染图标（使用 useMemo 缓存）
  const iconElement = useMemo(() => {
    if (urlInfo?.isValid && urlInfo.entityType === 'project') {
      // 项目显示文件夹图标
      return (
        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
        </svg>
      );
    } else if (urlInfo?.isValid && urlInfo.entityType === 'task' && task) {
      // 任务显示 checkbox
      const checkboxColorClass = task.done
        ? 'border-purple-500 bg-purple-500 dark:border-purple-400 dark:bg-purple-400'
        : task.vital && task.urgent
          ? 'border-ios-red dark:border-ios-dark-red'
          : task.vital
            ? 'border-ios-orange dark:border-ios-dark-orange'
            : task.urgent
              ? 'border-ios-blue dark:border-ios-dark-blue'
              : 'border-purple-300 dark:border-purple-600';

      return (
        <button
          onClick={handleTaskToggle}
          disabled={isUpdating}
          className={`w-4 h-4 mr-2 rounded-sm border flex items-center justify-center transition-all duration-200 ${checkboxColorClass} ${
            isUpdating
              ? 'opacity-50'
              : 'hover:scale-110 hover:shadow-md hover:brightness-110'
          }`}
          aria-label={task.done ? t('v3/common:task.markAsIncomplete') : t('v3/common:task.markAsComplete')}
        >
          {task.done && (
            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      );
    } else {
      // 默认显示 ✓ 图标
      return <span className="mr-2 text-current">{t('v3/common:icons.checkmark')}</span>;
    }
  }, [urlInfo, task, handleTaskToggle, isUpdating, t]);

  return (
    <span
      className={`${baseClasses} ${className}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e as any);
        }
      }}
    >
      {/* 图标 */}
      {iconElement}

      {/* 显示文本 */}
      <span>
        {displayText}
      </span>

      {/* 别名（如果存在） */}
      {alias && (
        <span className="ml-1 text-gray-500 dark:text-gray-400">
          | {alias}
        </span>
      )}
    </span>
  );
};
